import React from 'react';
import {
  Box,
  type Breakpoint,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  styled,
  type SxProps,
  Typography,
} from '@mui/material';
import { ReactComponent as CloseIcon } from 'assets/images/close-dialog.svg';

interface StyledDialogTitleProps {
  hasClose?: boolean;
  extraTitleOffset?: boolean;
  hasIcon?: boolean;
}

const DialogHeader = styled(Stack)(() => ({
  gap: '10px',
}));

const StyledDialogTitle = styled(DialogTitle, {
  shouldForwardProp: prop => prop !== 'hasClose' && prop !== 'extraTitleOffset' && prop !== 'hasIcon',
})<StyledDialogTitleProps>(({ theme, hasClose, extraTitleOffset, hasIcon }) => ({
  marginRight: hasClose ? (extraTitleOffset ? '34px' : '26px') : 0,
  gap: hasIcon ? theme.spacing(2) : theme.spacing(0),
  display: 'flex',
  alignItems: 'flex-start',
}));

const TitleIconWrapper = styled(Box)(() => ({
  height: '20px',
}));

const CloseButton = styled(IconButton)(() => ({
  position: 'absolute',
  right: 14,
  top: 24,
}));

interface CustomDialogProps {
  open: boolean;
  TitleIcon?: React.ReactNode;
  title?: string | React.ReactNode;
  aboveTitle?: React.ReactNode;
  bodyChildren?: React.ReactNode;
  onClose?: () => void;
  maxWidth?: Breakpoint | 'md-lg' | 'sm-md';
  subtitle?: string | React.ReactNode;
  dialogStyles?: SxProps;
  bodyStyles?: SxProps;
  buttonsChildren?: React.ReactNode;
  extraTitleOffset?: boolean;
  titleVariant?: 'regular' | 'small' | 'large';
}

export const CustomDialog = ({ TitleIcon, titleVariant = 'regular', ...props }: CustomDialogProps) => (
  <Dialog
    onClose={props.onClose}
    open={props.open}
    disableEscapeKeyDown={props.onClose === undefined}
    maxWidth={props.maxWidth as Breakpoint}
    fullWidth
    sx={props.dialogStyles}>
    {/* eslint-disable-next-line */}
    {(props.title || props.aboveTitle) && (
      <DialogHeader className="custom-dialog-header">
        {props.aboveTitle}
        {props.title && (
          <StyledDialogTitle
            color="#2A3040"
            className={`DialogTitle-${titleVariant}`}
            hasClose={props.onClose !== undefined}
            extraTitleOffset={props.extraTitleOffset}
            hasIcon={!!TitleIcon}>
            {TitleIcon && <TitleIconWrapper>{TitleIcon}</TitleIconWrapper>}
            {props.title}
          </StyledDialogTitle>
        )}
        {props.subtitle && (
          <Typography variant="body1" color="#525E7E">
            {props.subtitle}
          </Typography>
        )}
      </DialogHeader>
    )}
    {props.onClose !== undefined && (
      <CloseButton aria-label="close" onClick={props.onClose} id="close-btn">
        <CloseIcon />
      </CloseButton>
    )}
    {props.bodyChildren && <DialogContent sx={props.bodyStyles}>{props.bodyChildren}</DialogContent>}
    {props.buttonsChildren && <DialogActions>{props.buttonsChildren}</DialogActions>}
  </Dialog>
);
